import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Bell, CheckCircle, XCircle, Package, AlertTriangle, RotateCcw, FileText, CheckCheck, Trash2, Clock, Filter } from 'lucide-react';
import { API_URL } from '../Config';
export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadNotifications = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/notifications?page=${pageNum}&per_page=15`,
        { headers }
      );
      const newNotifs = res.data.data.data;
      if (pageNum === 1) {
        setNotifications(newNotifs);
      } else {
        setNotifications(prev => [...prev, ...newNotifs]);
      }
      setHasMore(res.data.data.current_page < res.data.data.last_page);
    } catch (err) {
      console.error('Erreur de chargement:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const clearAll = async () => {
    if (!window.confirm('Supprimer toutes les notifications ?')) return;
    try {
      await axios.delete(`${API_URL}/notifications/clear/all`, { headers });
      setNotifications([]);
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const clearRead = async () => {
    try {
      await axios.delete(`${API_URL}/notifications/clear/read`, { headers });
      setNotifications(prev => prev.filter(n => !n.is_read));
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/notifications/mark-all-read`, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API_URL}/notifications/${id}/read`, {}, { headers });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) {
      console.error('Erreur:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'demande_created': return <FileText size={20} />;
      case 'demande_validated': return <CheckCircle size={20} />;
      case 'demande_refused': return <XCircle size={20} />;
      case 'demande_ready': return <Package size={20} />;
      case 'stock_low': return <AlertTriangle size={20} />;
      case 'materiel_returned': return <RotateCcw size={20} />;
      default: return <Bell size={20} />;
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'demande_created': return 'bg-blue-100 text-blue-600';
      case 'demande_validated': return 'bg-green-100 text-green-600';
      case 'demande_refused': return 'bg-red-100 text-red-600';
      case 'demande_ready': return 'bg-emerald-100 text-emerald-600';
      case 'stock_low': return 'bg-orange-100 text-orange-600';
      case 'materiel_returned': return 'bg-purple-100 text-purple-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'unread') return !notif.is_read;
    if (filter === 'read') return notif.is_read;
    return true;
  });

  return (
    <div className="min-h-screen  p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Bell className="text-blue-600" size={32} />
            <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          </div>
          <p className="text-gray-600">Gérez toutes vos notifications en un seul endroit</p>
        </div>

        {/* Filters and Actions */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex gap-2">
              {['all', 'unread', 'read'].map((filterType) => (
                <button
                  key={filterType}
                  onClick={() => setFilter(filterType)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === filterType
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filterType === 'all' && 'Toutes'}
                  {filterType === 'unread' && 'Non lues'}
                  {filterType === 'read' && 'Lues'}
                </button>
              ))}
            </div>

            <div className="flex gap-2">
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-2 px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              >
                <CheckCheck size={16} />
                Tout marquer comme lu
              </button>
              <button
                onClick={clearRead}
                className="flex items-center gap-2 px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
              >
                <Trash2 size={16} />
                Supprimer les lues
              </button>
              <button
                onClick={clearAll}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={16} />
                Tout supprimer
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-3">
          {loading && notifications.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement des notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Bell className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-gray-600 text-lg">
                {filter === 'all' ? 'Aucune notification' : filter === 'unread' ? 'Aucune notification non lue' : 'Aucune notification lue'}
              </p>
            </div>
          ) : (
            filteredNotifications.map(notif => (
              <div
                key={notif.id}
                className={`bg-white rounded-lg shadow-sm p-4 transition-all hover:shadow-md ${
                  !notif.is_read ? 'border-l-4 border-blue-600' : ''
                }`}
              >
                <div className="flex gap-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${getNotificationColor(notif.type)}`}>
                    {getNotificationIcon(notif.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-semibold text-gray-900">{notif.title}</h3>
                      {!notif.is_read && (
                        <span className="flex-shrink-0 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          Non lu
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{notif.message}</p>
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {format(new Date(notif.created_at), 'dd MMM yyyy à HH:mm', { locale: fr })}
                      </span>
                      <span className="px-2 py-1 bg-gray-100 rounded-full">
                        {notif.type.replace('_', ' ')}
                      </span>
                      {!notif.is_read && (
                        <button
                          onClick={() => markAsRead(notif.id)}
                          className="text-blue-600 hover:text-blue-700 font-medium ml-auto"
                        >
                          Marquer comme lu
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Load More */}
        {hasMore && filteredNotifications.length > 0 && (
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setPage(p => p + 1);
                loadNotifications(page + 1);
              }}
              disabled={loading}
              className="px-6 py-3 bg-white border border-gray-300 rounded-lg text-gray-700 hover: font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
            >
              {loading ? 'Chargement...' : 'Charger plus de notifications'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}